import { Box, Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, FormGroup, FormLabel, IconButton, Modal, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import { ImportFile, getExternalImportFiles, updateExternalImportRows } from "./ProductImportRepository";
import { useEffect, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import { reactEvents } from "../../../bridge/reactEvents";
import { useTranslation } from "react-i18next";

export interface ExternalImportSettings {
  productImportId: number
  useExistingPurchaseCost: boolean
  useExistingSalesPrice: boolean
  useExistingPreviousPrice: boolean
  useMainGroupAndBrand: boolean
}

export function ExternalImportFileList() {

  // PROPS, HOOKS & STATE

  const [loading, setLoading] = useState(false);
  const [imports, setImports] = useState<ImportFile[]>([]);
  const [openSettingModal, setOpenSettingModal] = useState(false);
  const [selectedImportId, setSelectedImportId] = useState(0);
  const [externalImportSettings, setExternalImportsettings] = useState<ExternalImportSettings>({
    productImportId: 0,
    useExistingPreviousPrice: false,
    useExistingPurchaseCost: false,
    useExistingSalesPrice: false,
    useMainGroupAndBrand: false
  });

  const { t, i18n } = useTranslation();

  useEffect(() => {
    setLoading(true);
    getExternalImportFiles().then((data) => {
      setImports(data);
      setLoading(false);
    });
  }, []);

  // FUNCTIONS

  function handleOpenSettingsModal(importId: number) {
    setSelectedImportId(importId);
    setExternalImportsettings({ ...externalImportSettings, productImportId: importId });
    setOpenSettingModal(true);
  }

  const dispatchDataAndGoToEditor = () => {

    if (selectedImportId < 1) {
      console.error("No importId Assigned in ExternalImportFileList.tsx. Cannot navigate to editor.");
      return;
    }

    setOpenSettingModal(false);
    // TODO: (in future) Centralize loading to a global context and component
    setLoading(true);

    const putData = async () => {
      await updateExternalImportRows(externalImportSettings);
    };

    if (externalImportSettings.useExistingPreviousPrice || externalImportSettings.useExistingPurchaseCost ||
      externalImportSettings.useExistingSalesPrice || externalImportSettings.useMainGroupAndBrand) {
      putData().then((_) => {
        reactEvents.goToImportEditorById.dispatch(selectedImportId);
        setLoading(false);
      });
    }
    else {
      reactEvents.goToImportEditorById.dispatch(selectedImportId);
    }
  };

  const setPurchaseCostSetting = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExternalImportsettings({ ...externalImportSettings, useExistingPurchaseCost: !externalImportSettings.useExistingPurchaseCost });
  };

  const setSalesPriceSetting = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExternalImportsettings({ ...externalImportSettings, useExistingSalesPrice: !externalImportSettings.useExistingSalesPrice });
  };

  const setPreviousPriceSetting = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExternalImportsettings({ ...externalImportSettings, useExistingPreviousPrice: !externalImportSettings.useExistingPreviousPrice});
  };

  const setMainGroupAndBrandSetting = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedSetting = event.target.value === 'true' ? true : false;
    setExternalImportsettings({ ...externalImportSettings, useMainGroupAndBrand: updatedSetting });
  };

  // STYLING

  const tableCellStyle = {
    paddingInline: "30 px",
    padding: "15px"
  };

  const tableRowStyle = {
    borderBottom: "1px solid lightgrey"
  };

  const settingModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50rem',
    bgcolor: 'White',
    boxShadow: 24,
    p: 4
  };

  const formLabelControlStyle = {
    display: "flex",
    justifyContent: "end",
    margin: 0,
    padding: 0
  };


  return (
    <div style={{ height: "100%", width: "100%", display: "flex" }}>
      {loading ? <div>
        <CircularProgress color="primary" />
      </div> :
        <div style={{ display: "flex", height: "100%", width: "100%", flexDirection: "column" }}>
          {/* TABLE OF EXTERNAL IMPORTS */}
          <table
            style={{ flexDirection: "column", flex: 1, placeContent: "center", margin: "30px" }}
          >
            <thead>
              <tr style={tableRowStyle}>
                <th style={tableCellStyle}>{t('productimport.companyCode')}</th>
                <th style={tableCellStyle}>{t('productimport.oldestImportDate')}</th>
                <th style={tableCellStyle}>{t('productimport.newestImportDate')}</th>
                <th style={tableCellStyle}>{t('productimport.totalProductsQuantity')}</th>
                <th style={tableCellStyle}>{t('productimport.totalOrders')}</th>
                <th style={tableCellStyle}>{t('productimport.totalOrderedQuantity')}</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(imports) && imports.map((file, idx) => (
                <tr key={idx} style={tableRowStyle}>
                  <td style={tableCellStyle}>
                    <a style={{ color: 'blue' }} onClick={() => handleOpenSettingsModal(file.id)}>{file.fileName}</a>
                  </td>
                  <td style={tableCellStyle}>{format(new Date(file.oldestImportDate), 'yyyy-MM-dd')}</td>
                  <td style={tableCellStyle}>{format(new Date(file.importDate), 'yyyy-MM-dd')}</td>
                  <td style={tableCellStyle}>{file.totalProductQuantity ?? 0}</td>
                  <td style={tableCellStyle}>{file.totalOrders ?? 0}</td>
                  <td style={tableCellStyle}>{file.totalOrderedQuantity ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* SETTINGS MODAL */}
          <Modal open={openSettingModal}
            onClose={() => setOpenSettingModal(false)}
          >
            <Box sx={settingModalStyle}>
              <Stack direction={"row"} sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h4">
                  {t("common.settings")}
                </Typography>
                <IconButton size="large" onClick={() => setOpenSettingModal(false)}><CloseIcon sx={{ height: "20px", width: "20px" }} /></IconButton>
              </Stack>
              <Divider sx={{ marginBlock: 2 }} />
              <FormControl sx={{ width: "100%" }}>
                <FormGroup sx={{ marginBottom: 2, display: "flex", justifyContent: "end" }}>
                  <Stack spacing={2} direction={"row"} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: 14 }}>{t('productimport.trygetpricesfromproduct')}</Typography>
                    <FormControlLabel sx={formLabelControlStyle} control={<Checkbox checked={externalImportSettings.useExistingPurchaseCost} onChange={setPurchaseCostSetting} color="secondary" size="medium" />} label={t('productimport.purchaseCost')} labelPlacement="start" />
                  </Stack>
                  <FormControlLabel sx={formLabelControlStyle} control={<Checkbox checked={externalImportSettings.useExistingSalesPrice} onChange={setSalesPriceSetting} color="secondary" size="medium" />} label={t('productimport.salesPrice')} labelPlacement="start" />
                  <FormControlLabel sx={formLabelControlStyle} control={<Checkbox checked={externalImportSettings.useExistingPreviousPrice} onChange={setPreviousPriceSetting} color="secondary" size="medium" />} label={t('productimport.previousPrice')} labelPlacement="start" />
                </FormGroup>

                <RadioGroup
                  value={externalImportSettings.useMainGroupAndBrand}
                  onChange={setMainGroupAndBrandSetting}
                  defaultValue={false}
                >
                  <Stack spacing={2} direction={"row"} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: 14 }}>{t("productimport.getitemgroupfrom")}</Typography>
                    <FormControlLabel sx={formLabelControlStyle} value={false} control={<Radio color="secondary" size="medium" />} label={t('productimport.itemgroup')} labelPlacement="start" />
                  </Stack>
                  <FormControlLabel sx={formLabelControlStyle} value={true} control={<Radio color="secondary" size="medium" />} label={t('brand.brandcode') + " & " + t('maingroup.maingroupcode')} labelPlacement="start" />
                </RadioGroup>
              </FormControl>
              <Box sx={{ marginTop: 3, display: "flex", justifyContent: "center" }}>
                <Button color="secondary" onClick={dispatchDataAndGoToEditor} variant="contained">{t('common.confirm')}</Button>
              </Box>
            </Box>
          </Modal>
        </div>
      }
    </div>
  );
}

